/**
 * About Page - /about
 *
 * v3.94 - P-01: About page — founding story, mission, team, tools - 2026-03-20
 *
 * Route always exists regardless of features.about flag.
 * Flag only controls nav/footer link visibility.
 * Direct URL /about always works, never 404s.
 *
 * Founder photo: placeholder Avatar (BM initials) until real headshot is provided.
 * To replace: swap <Avatar> with <img src="/founder-photo.jpg" /> and update styles.
 */
import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Avatar,
  Chip,
  Divider,
  Button,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useFeatureFlags, getPrimaryCTA } from '../contexts/FeatureFlagContext';
import { analytics } from '../utils/analytics';

const About: React.FC = () => {
  const navigate = useNavigate();
  const { siteMode, flagsLoaded } = useFeatureFlags();
  const cta = getPrimaryCTA(siteMode);

  const handleCTAClick = () => {
    analytics.ctaClick('hero', siteMode);
    navigate(cta.href);
  };

  // Tools MyLojic connects to
  const tools = ['QuickBooks', 'Xero', 'Stripe', 'Shopify', 'Sage'];

  return (
    <Box>
      {/* Hero */}
      <Box sx={{ bgcolor: 'background.paper', py: { xs: 6, md: 10 } }}>
        <Container maxWidth="md">
          <Typography
            variant="h2"
            sx={{ fontWeight: 800, mb: 2, color: 'text.primary', fontSize: { xs: '2rem', md: '3rem' } }}
          >
            Why we built MyLojic
          </Typography>
          <Typography
            variant="h6"
            sx={{ color: 'text.secondary', fontWeight: 400, lineHeight: 1.7, maxWidth: 620 }}
          >
            The tools that give Fortune 500 companies clarity about their business have always been out of reach
            for the owners who need them most.
          </Typography>
        </Container>
      </Box>

      {/* Founding Story */}
      <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: 'background.default' }}>
        <Container maxWidth="md">
          <Grid container spacing={6} alignItems="flex-start">
            {/* Founder */}
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', md: 'center' }, gap: 1.5 }}>
                {/* Placeholder avatar — replace with <img> when headshot is available */}
                <Avatar
                  sx={{
                    width: 120,
                    height: 120,
                    bgcolor: 'primary.main',
                    fontSize: '2.5rem',
                    fontWeight: 700,
                  }}
                >
                  BM
                </Avatar>
                <Box sx={{ textAlign: { md: 'center' } }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Branko Milojic
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Founder & CEO
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Story */}
            <Grid item xs={12} md={9}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: 'text.primary' }}>
                The founding story
              </Typography>

              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 2 }}>
                I spent years in the enterprise software world, watching companies like Tableau and Salesforce
                sell analytics to organizations that already had teams of analysts to run them. The tools were
                impressive. The customers could afford them. And the owners who actually needed clear answers
                about their business, the ones running 20-person distributors, managing job sites, watching
                cash flow manually every Friday morning, never saw any of it.
              </Typography>

              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 2 }}>
                Not because the data wasn't there. QuickBooks tracks everything. Xero tracks everything. Stripe
                tracks everything. The data existed. What didn't exist was a way to get answers out of it without
                hiring someone who knew what they were doing.
              </Typography>

              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 2 }}>
                That's the gap MyLojic fills. You connect your tools once. You ask questions in plain English.
                You get direct answers, not dashboards to build, not spreadsheets to decipher. Your numbers,
                your language, every time.
              </Typography>

              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                We're in private pilot right now, working closely with a small group of SMB owners to make sure
                what we're building actually works for the way they run their business.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Divider />

      {/* Mission */}
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="md">
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: 'text.primary' }}>
            Our mission
          </Typography>
          <Typography
            variant="h6"
            sx={{ color: 'text.secondary', fontWeight: 400, lineHeight: 1.7, maxWidth: 580, fontStyle: 'italic' }}
          >
            Every SMB owner deserves the same visibility into their business that their Fortune 500 competitors
            pay analysts to produce, without hiring anyone.
          </Typography>
        </Container>
      </Box>

      <Divider />

      {/* What we're building */}
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: 'background.default' }}>
        <Container maxWidth="md">
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: 'text.primary' }}>
            What we're building
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 2 }}>
            MyLojic is a zero-lift analytics platform. You connect your existing tools, answer a few questions
            about your business, and the platform does everything else automatically.
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 2 }}>
            No dashboards to configure. No reports to build. No analysts to hire. Your data is always there when
            you need it, in language you actually understand.
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
            We're currently in private pilot, onboarding a limited number of SMBs to refine the product before
            opening more broadly.
          </Typography>
        </Container>
      </Box>

      <Divider />

      {/* Tools */}
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="md">
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}>
            Works with your tools
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            If your business runs on these tools, MyLojic reads them.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {tools.map(tool => (
              <Chip
                key={tool}
                label={tool}
                variant="outlined"
                sx={{ fontWeight: 600, borderColor: 'divider' }}
              />
            ))}
          </Box>
        </Container>
      </Box>

      <Divider />

      {/* Contact */}
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: 'background.default' }}>
        <Container maxWidth="md">
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}>
            Get in touch
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 1 }}>
            Questions, press enquiries, or partnership interest:
          </Typography>
          <Typography variant="body1">
            <a
              href="mailto:branko.milojic@dataedgesvc.com"
              style={{ color: 'inherit', fontWeight: 600 }}
            >
              branko.milojic@dataedgesvc.com
            </a>
          </Typography>
        </Container>
      </Box>

      {/* CTA */}
      {flagsLoaded && (
        <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: 'primary.main', textAlign: 'center' }}>
          <Container maxWidth="sm">
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#fff', mb: 2 }}>
              Want in on the pilot?
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)', mb: 4 }}>
              We're working with a small group of SMB owners this quarter. Apply for early access.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={handleCTAClick}
              sx={{
                bgcolor: 'accent.main',
                color: 'accent.contrastText',
                py: 1.75,
                px: 5,
                fontWeight: 700,
                '&:hover': { bgcolor: 'accent.dark' },
              }}
            >
              {cta.text}
            </Button>
          </Container>
        </Box>
      )}
    </Box>
  );
};

export default About;
